import React, {
  createContext,
  ReactNode,
  useEffect,
  useReducer,
  useState
} from 'react'

import {
  ChatHistoryLoadingState,
  Conversation,
  CosmosDBHealth,
  CosmosDBStatus,
  Feedback,
  FrontendSettings,
  frontendSettings,
  historyEnsure,
  historyList
} from '../api'
import { authenticatedFetch } from '../utils/authApi'
import { useAuth } from '../components/Auth/AuthProvider'
import { buildApiUrl } from '../config/apiConfig'
import { debugLog } from '../utils/debugLogger'

import { appStateReducer } from './AppReducer'

export interface AppState {
  isChatHistoryOpen: boolean
  chatHistoryLoadingState: ChatHistoryLoadingState
  isCosmosDBAvailable: CosmosDBHealth
  chatHistory: Conversation[] | null
  chatHistoryContinuationToken: string | null  // For pagination
  chatHistoryHasMore: boolean  // Whether more conversations exist
  filteredChatHistory: Conversation[] | null
  currentChat: Conversation | null
  frontendSettings: FrontendSettings | null
  feedbackState: { [answerId: string]: Feedback.Neutral | Feedback.Positive | Feedback.Negative }
  isLoading: boolean;
  answerExecResult: { [answerId: string]: [] }
  // Model selection
  models: string[];
  modelOptions?: { id: string; label: string }[];
  selectedModel: string;
}

export type Action =
  | { type: 'TOGGLE_CHAT_HISTORY' }
  | { type: 'SET_COSMOSDB_STATUS'; payload: CosmosDBHealth }
  | { type: 'UPDATE_CHAT_HISTORY_LOADING_STATE'; payload: ChatHistoryLoadingState }
  | { type: 'UPDATE_CURRENT_CHAT'; payload: Conversation | null }
  | { type: 'UPDATE_FILTERED_CHAT_HISTORY'; payload: Conversation[] | null }
  | { type: 'UPDATE_CHAT_HISTORY'; payload: Conversation }
  | { type: 'UPDATE_CHAT_TITLE'; payload: Conversation }
  | { type: 'DELETE_CHAT_ENTRY'; payload: string }
  | { type: 'DELETE_CHAT_HISTORY' }
  | { type: 'DELETE_CURRENT_CHAT_MESSAGES'; payload: string }
  | { type: 'FETCH_CHAT_HISTORY'; payload: Conversation[] | null }
  | { type: 'SET_CHAT_HISTORY_PAGINATION'; payload: { continuationToken: string | null, hasMore: boolean } }
  | { type: 'APPEND_CHAT_HISTORY'; payload: Conversation[] }
  | { type: 'FETCH_FRONTEND_SETTINGS'; payload: FrontendSettings | null }
  | {
    type: 'SET_FEEDBACK_STATE'
    payload: { answerId: string; feedback: Feedback.Positive | Feedback.Negative | Feedback.Neutral }
  }
  | { type: 'GET_FEEDBACK_STATE'; payload: string }
  | { type: 'SET_ANSWER_EXEC_RESULT'; payload: { answerId: string, exec_result: [] } }
  | { type: 'SET_MODELS'; payload: string[] }
  | { type: 'SET_MODEL_OPTIONS'; payload: { id: string; label: string }[] }
  | { type: 'SET_SELECTED_MODEL'; payload: string }

const initialState: AppState = {
  isChatHistoryOpen: false,
  chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
  chatHistory: null,
  chatHistoryContinuationToken: null,
  chatHistoryHasMore: false,
  filteredChatHistory: null,
  currentChat: null,
  isCosmosDBAvailable: {
    cosmosDB: false,
    status: CosmosDBStatus.NotConfigured
  },
  frontendSettings: null,
  feedbackState: {},
  isLoading: true,
  answerExecResult: {},
  // Model selection
  models: [],
  modelOptions: undefined,
  selectedModel: '',
}

// Global frontend settings request deduplication
// Prevents duplicate /frontend_settings calls during page load/refresh
let _frontendSettingsPromise: Promise<FrontendSettings | null> | null = null;
let _frontendSettingsCache: FrontendSettings | null = null;

async function fetchFrontendSettingsOnce(): Promise<FrontendSettings | null> {
  // Return cached result if available
  if (_frontendSettingsCache) {
    return _frontendSettingsCache;
  }
  
  // Deduplicate concurrent requests
  if (_frontendSettingsPromise) {
    return _frontendSettingsPromise;
  }
  
  _frontendSettingsPromise = (async () => {
    try {
      const response = await fetch(buildApiUrl('/frontend_settings'), { credentials: 'include' });
      if (response.ok) {
        const settings = await response.json();
        _frontendSettingsCache = settings;
        return settings;
      }
      return null;
    } catch {
      return null;
    } finally {
      // Clear promise after completion (cache remains)
      _frontendSettingsPromise = null;
    }
  })();
  
  return _frontendSettingsPromise;
}

export const AppStateContext = createContext<
  | {
    state: AppState
    dispatch: React.Dispatch<Action>
  }
  | undefined
>(undefined)

type AppStateProviderProps = {
  children: ReactNode
}

export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState)
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [hasLoadedData, setHasLoadedData] = useState(false)
  const [hasLoadedFrontendSettings, setHasLoadedFrontendSettings] = useState(false)
  
  // Ref to track current frontend settings for merging in async callbacks
  const frontendSettingsRef = React.useRef(state.frontendSettings)
  React.useEffect(() => {
    frontendSettingsRef.current = state.frontendSettings
  }, [state.frontendSettings])

  // Load frontend settings immediately (for login page logo, etc.) - doesn't require auth
  useEffect(() => {
    if (hasLoadedFrontendSettings) return
    
    const loadFrontendSettings = async () => {
      try {
        // Use global deduplication to prevent duplicate calls during page load
        const settings = await fetchFrontendSettingsOnce();
        if (settings) {
          dispatch({ type: 'FETCH_FRONTEND_SETTINGS', payload: settings })
          
          // Update document title
          if (settings?.ui?.page_title) {
            document.title = settings.ui.page_title
          }
          setHasLoadedFrontendSettings(true)
          
          // Log app version for debugging cache issues (these properties may exist on the raw response)
          const rawSettings = settings as Record<string, unknown>;
          if (rawSettings.app_version || rawSettings.app_startup_time) {
            debugLog(`[App] 🚀 Backend version: ${rawSettings.app_version || 'unknown'} (started: ${rawSettings.app_startup_time || 'unknown'})`)
          }
          debugLog('[AppStateProvider] Frontend settings loaded (pre-auth)')
        }
      } catch (error) {
        console.error('[AppStateProvider] Failed to load frontend settings:', error)
      }
    }
    
    loadFrontendSettings()
  }, [hasLoadedFrontendSettings])

  // Load user-specific data only after authentication
  useEffect(() => {
    // Wait for auth to finish loading before doing anything
    if (authLoading) {
      debugLog('[AppStateProvider] Waiting for auth to complete...')
      return
    }
    
    // Skip if we've already loaded data
    if (hasLoadedData) {
      return
    }
    
    // Skip user data if not authenticated (let ProtectedRoute handle redirection)
    if (!isAuthenticated) {
      debugLog('[AppStateProvider] User not authenticated, skipping user data load')
      dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
      return
    }

    // Simplified bootstrap initialization - replaces multiple API calls
    const initializeApp = async () => {
      dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Loading })
      debugLog('[AppStateProvider] User authenticated, loading bootstrap data...')
      
      try {
        const { getBootstrapData } = await import('../api/api')
        const { getUserPreferences } = await import('../api/dataSourceApi')
        
        // Load bootstrap data and user preferences in parallel
        const [bootstrapData, userPreferences] = await Promise.allSettled([
          getBootstrapData(),
          getUserPreferences()
        ])
        
        if (bootstrapData.status === 'fulfilled' && bootstrapData.value) {
          // Set all data from bootstrap response
          dispatch({ type: 'SET_MODELS', payload: bootstrapData.value.models.models || [] })
          dispatch({ type: 'SET_MODEL_OPTIONS', payload: bootstrapData.value.models.model_options || [] })
          
          // Set selected model from user preferences or fallback to default
          let selectedModel = ''
          if (userPreferences.status === 'fulfilled') {
            if (userPreferences.value?.selected_model) {
              selectedModel = userPreferences.value.selected_model
            } else {
              // Use default model from bootstrap data
              selectedModel = bootstrapData.value.models.default_model || 
                              (bootstrapData.value.models.models && bootstrapData.value.models.models[0]) || 
                              '' // No hardcoded fallback - use empty string if no models configured
            }
          } else {
            // Use default model from bootstrap data
            selectedModel = bootstrapData.value.models.default_model || 
                            (bootstrapData.value.models.models && bootstrapData.value.models.models[0]) || 
                            '' // No hardcoded fallback - use empty string if no models configured
          }
          dispatch({ type: 'SET_SELECTED_MODEL', payload: selectedModel })
          
          // Only update frontend settings from bootstrap if they contain more data (logo, etc.)
          // This prevents overwriting pre-auth loaded settings with potentially incomplete bootstrap data
          const existingSettings = frontendSettingsRef.current;
          const bootstrapSettings = bootstrapData.value.frontend_settings;
          
          // Merge settings: prefer bootstrap values but keep existing logo/favicon if bootstrap doesn't have them
          if (bootstrapSettings) {
            const mergedSettings = {
              ...existingSettings,
              ...bootstrapSettings,
              ui: {
                ...existingSettings?.ui,
                ...bootstrapSettings.ui,
                // Preserve logo/favicon/chat_logo if they exist and bootstrap doesn't provide them
                logo: bootstrapSettings.ui?.logo || existingSettings?.ui?.logo,
                favicon: bootstrapSettings.ui?.favicon || existingSettings?.ui?.favicon,
                chat_logo: bootstrapSettings.ui?.chat_logo || existingSettings?.ui?.chat_logo,
              }
            };
            dispatch({ type: 'FETCH_FRONTEND_SETTINGS', payload: mergedSettings })
          }
          
          // Update document title when frontend settings are loaded
          if (bootstrapData.value.frontend_settings?.ui?.page_title) {
            document.title = bootstrapData.value.frontend_settings.ui.page_title
          }
          
          // Set CosmosDB status first
          dispatch({ type: 'SET_COSMOSDB_STATUS', payload: bootstrapData.value.cosmos_db })
          
          // Set chat history (can be empty array)
          dispatch({ type: 'FETCH_CHAT_HISTORY', payload: bootstrapData.value.conversations || [] })
          
          // Set pagination state from bootstrap
          dispatch({ 
            type: 'SET_CHAT_HISTORY_PAGINATION', 
            payload: { 
              continuationToken: bootstrapData.value.continuation_token || null,
              hasMore: bootstrapData.value.has_more_conversations || false
            }
          })
          debugLog('[AppStateProvider] Chat history loaded:', bootstrapData.value.conversations?.length || 0, 'conversations')
          
          // Set loading state based on CosmosDB availability (empty conversations is still success)
          if (bootstrapData.value.cosmos_db.cosmosDB) {
            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Success })
          } else {
            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
          }
          
          setHasLoadedData(true)
        } else {
          // Fallback if bootstrap fails
          dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
          dispatch({ type: 'SET_COSMOSDB_STATUS', payload: { cosmosDB: false, status: CosmosDBStatus.NotConfigured } })
          console.error('Failed to load bootstrap data')
        }
      } catch (error) {
        dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
        dispatch({ type: 'SET_COSMOSDB_STATUS', payload: { cosmosDB: false, status: CosmosDBStatus.NotConfigured } })
        console.error('Error during app initialization:', error)
      }
    }
    
    initializeApp()
  }, [isAuthenticated, authLoading, hasLoadedData])

  useEffect(() => {
    // Listen for manual model updates triggered by cache clearing
    const handleModelsUpdate = (e: any) => {
      if (e?.detail) {
        dispatch({ type: 'SET_MODELS', payload: e.detail.models || [] })
        dispatch({ type: 'SET_MODEL_OPTIONS', payload: e.detail.model_options || [] })
        if (e.detail.default_model) {
          dispatch({ type: 'SET_SELECTED_MODEL', payload: e.detail.default_model })
        }
        debugLog('Models updated from cache clear:', e.detail)
      }
    }

    window.addEventListener('modelsUpdated', handleModelsUpdate)
    
    return () => {
      window.removeEventListener('modelsUpdated', handleModelsUpdate)
    }
  }, [])

  useEffect(() => {
    // Listen for manual updates triggered by admin uploads
    const handler = (e: any) => {
      if (e?.detail) {
        dispatch({ type: 'FETCH_FRONTEND_SETTINGS', payload: e.detail as FrontendSettings })
        
        // Update document title when settings are manually updated
        if (e.detail?.ui?.page_title) {
          debugLog('Setting document title from manual update:', e.detail.ui.page_title)
          document.title = e.detail.ui.page_title
        }
      }
    }
    window.addEventListener('frontendSettingsUpdated', handler as EventListener)
    return () => window.removeEventListener('frontendSettingsUpdated', handler as EventListener)
  }, [])

  return <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>
}

// Update document favicon and title when frontend settings change
// This is outside the provider component to avoid re-registering listeners.
(() => {
  try {
    if (typeof window !== 'undefined') {
      window.addEventListener('load', () => {
        // no-op on load
      })
    }
  } catch (e) {
    // ignore
  }
})()
